import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Chip,
  Fab,
  useMediaQuery,
  useTheme,
  Avatar,
  Stack,
  alpha,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Image as ImageIcon,
  Public as PublicIcon,
  CheckCircle as CheckCircleIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { env } from '../../utils/env';
import atendimentoFigurinhaService from '../../services/atendimentoFigurinhaService';
import { AtendimentoFigurinha } from '../../types/atendimento';
import ImageUploadCrop from '../../components/common/ImageUploadCrop';
import { usePermissions } from '../../contexts/AuthContext';

interface FormState {
  file: File | null;
  preview: string | null;
  ativo: boolean;
  compartilhar: boolean;
}

const formVazio: FormState = { file: null, preview: null, ativo: true, compartilhar: true };

interface KpiCardProps {
  titulo: string;
  valor: string | number;
  subtitulo?: string;
  icone: React.ReactNode;
  cor: string;
  bgCor: string;
}

const KpiCard: React.FC<KpiCardProps> = ({ titulo, valor, subtitulo, icone, cor, bgCor }) => (
  <Card elevation={0} sx={{ height: '100%', borderRadius: 2, border: (t) => `1px solid ${alpha(t.palette.divider, 0.8)}` }}>
    <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
        <Box minWidth={0}>
          <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>{titulo}</Typography>
          <Typography variant="h6" fontWeight={800} color={cor} lineHeight={1.2}>{valor}</Typography>
          {subtitulo && <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>{subtitulo}</Typography>}
        </Box>
        <Avatar sx={{ bgcolor: bgCor, width: 34, height: 34, '& .MuiSvgIcon-root': { fontSize: 18 } }}>{icone}</Avatar>
      </Box>
    </CardContent>
  </Card>
);

const getArquivoUrl = (path: string) => {
  if (path.startsWith('http') || path.startsWith('data:')) return path;
  return `${env.API_URL.replace(/\/$/, '')}${path}`;
};

const FigurinhasPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();
  const { can } = usePermissions();
  const podeAdicionarFigurinha = can('atendimento.adicionar_figurinha');
  const podeEditarFigurinha = can('atendimento.figurinhas.editar');
  const podeDeletarFigurinha = can('atendimento.figurinhas.deletar');

  const [figurinhas, setFigurinhas] = useState<AtendimentoFigurinha[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogAberto, setDialogAberto] = useState(false);
  const [editando, setEditando] = useState<AtendimentoFigurinha | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [form, setForm] = useState<FormState>(formVazio);
  const [cropOpen, setCropOpen] = useState(false);

  const totalAtivas = useMemo(() => figurinhas.filter((item) => item.ativo).length, [figurinhas]);
  const totalCompartilhadas = useMemo(() => figurinhas.filter((item) => item.compartilhar).length, [figurinhas]);

  const carregarFigurinhas = useCallback(async () => {
    setLoading(true);
    try {
      const response = await atendimentoFigurinhaService.getAll({ page: 1, limit: 200 });
      setFigurinhas(response.success ? (response.data || []) : []);
    } catch (error) {
      console.error('Erro ao buscar figurinhas:', error);
      enqueueSnackbar('Erro ao buscar figurinhas', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    carregarFigurinhas();
  }, [carregarFigurinhas]);

  useEffect(() => {
    return () => {
      if (form.preview?.startsWith('blob:')) URL.revokeObjectURL(form.preview);
    };
  }, [form.preview]);

  const abrirNova = () => {
    setEditando(null);
    setForm(formVazio);
    setDialogAberto(true);
  };

  const abrirEditar = (figurinha: AtendimentoFigurinha) => {
    if (!podeEditarFigurinha) return;
    setEditando(figurinha);
    setForm({
      file: null,
      preview: getArquivoUrl(figurinha.caminho_arquivo),
      ativo: figurinha.ativo,
      compartilhar: figurinha.compartilhar,
    });
    setDialogAberto(true);
  };

  const fecharDialog = () => {
    setDialogAberto(false);
    setEditando(null);
    setForm(formVazio);
  };

  const handleImageCropSave = (croppedImageBlob: Blob, croppedImageUrl: string) => {
    const extension = croppedImageBlob.type === 'image/png' ? 'png' :
      croppedImageBlob.type === 'image/webp' ? 'webp' : 'jpg';
    
    const file = new File([croppedImageBlob], `figurinha.${extension}`, {
      type: croppedImageBlob.type
    });

    if (form.preview?.startsWith('blob:')) URL.revokeObjectURL(form.preview);

    setForm((prev) => ({ ...prev, file, preview: croppedImageUrl }));
    setCropOpen(false);
  };

  const handleSalvar = async () => {
    if (editando && !podeEditarFigurinha) return;
    if (!editando && !podeAdicionarFigurinha) return;

    if (!editando && !form.file) {
      enqueueSnackbar('Selecione uma imagem para a figurinha.', { variant: 'warning' });
      return;
    }

    try {
      setSalvando(true);

      if (editando) {
        await atendimentoFigurinhaService.update(editando.id, {
          file: form.file,
          ativo: form.ativo,
          compartilhar: form.compartilhar,
        });
        enqueueSnackbar('Figurinha atualizada com sucesso!', { variant: 'success' });
      } else if (form.file) {
        await atendimentoFigurinhaService.create({
          file: form.file,
          ativo: form.ativo,
          compartilhar: form.compartilhar,
        });
        enqueueSnackbar('Figurinha cadastrada com sucesso!', { variant: 'success' });
      }

      fecharDialog();
      carregarFigurinhas();
    } catch (error: any) {
      console.error('Erro ao salvar figurinha:', error);
      enqueueSnackbar(error?.response?.data?.message || 'Erro ao salvar figurinha.', { variant: 'error' });
    } finally {
      setSalvando(false);
    }
  };

  const handleExcluir = async (figurinha: AtendimentoFigurinha) => {
    if (!podeDeletarFigurinha) return;
    if (!window.confirm('Tem certeza que deseja excluir esta figurinha?')) return;

    try {
      await atendimentoFigurinhaService.delete(figurinha.id);
      enqueueSnackbar('Figurinha excluida com sucesso!', { variant: 'success' });
      carregarFigurinhas();
    } catch (error) {
      console.error('Erro ao excluir figurinha:', error);
      enqueueSnackbar('Erro ao excluir figurinha.', { variant: 'error' });
    }
  };

  const handleToggle = async (figurinha: AtendimentoFigurinha, field: 'ativo' | 'compartilhar') => {
    if (!podeEditarFigurinha) return;
    try {
      const response = await atendimentoFigurinhaService.update(figurinha.id, { [field]: !figurinha[field] });

      if (response.success && response.data) {
        setFigurinhas((prev) => prev.map((item) => item.id === figurinha.id ? response.data! : item));
      } else {
        carregarFigurinhas();
      }
    } catch (error) {
      enqueueSnackbar('Erro ao alterar figurinha.', { variant: 'error' });
    }
  };

  const headerGradient =
    theme.palette.mode === 'dark'
      ? `linear-gradient(135deg, ${alpha(theme.palette.success.dark, 0.35)} 0%, ${alpha(theme.palette.primary.main, 0.25)} 50%, ${alpha('#1a1a2e', 0.9)} 100%)`
      : `linear-gradient(135deg, ${alpha('#e8f5e9', 0.95)} 0%, ${alpha(theme.palette.primary.light, 0.15)} 45%, ${alpha(theme.palette.primary.main, 0.08)} 100%)`;

  return (
    <Box sx={{ p: { xs: 1, md: 1.5 }, mx: 'auto' }}>
      <Paper
        elevation={0}
        sx={{
          mb: 1.5,
          p: { xs: 1.25, md: 1.5 },
          borderRadius: 2,
          background: headerGradient,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <Box sx={{ position: 'absolute', top: -20, right: -10, opacity: 0.08, transform: 'rotate(15deg)' }}>
          <ImageIcon sx={{ fontSize: 120, color: theme.palette.success.main }} />
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={1.25}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Avatar
              sx={{
                width: 40,
                height: 40,
                background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
                boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.35)}`,
              }}
            >
              <ImageIcon sx={{ fontSize: 22 }} />
            </Avatar>
            <Box flex={1} minWidth={0}>
              <Typography variant="h6" fontWeight={800} lineHeight={1.2}>Figurinhas</Typography>
              <Typography variant="caption" color="text.secondary">Imagens salvas para enviar no atendimento</Typography>
            </Box>
          </Stack>
          {!isMobile && podeAdicionarFigurinha && (
            <Button variant="contained" startIcon={<AddIcon />} size="small" onClick={abrirNova}>
              Nova Figurinha
            </Button>
          )}
        </Stack>
      </Paper>

      {!loading && figurinhas.length > 0 && (
        <Grid container spacing={1} mb={1.5}>
          <Grid item xs={6} sm={4}>
            <KpiCard titulo="Total" valor={figurinhas.length} icone={<ImageIcon sx={{ color: theme.palette.primary.main }} />} cor={theme.palette.primary.main} bgCor={alpha(theme.palette.primary.main, 0.12)} />
          </Grid>
          <Grid item xs={6} sm={4}>
            <KpiCard titulo="Ativas" valor={totalAtivas} subtitulo={`${figurinhas.length ? Math.round((totalAtivas / figurinhas.length) * 100) : 0}% do total`} icone={<CheckCircleIcon sx={{ color: theme.palette.success.main }} />} cor={theme.palette.success.main} bgCor={alpha(theme.palette.success.main, 0.12)} />
          </Grid>
          <Grid item xs={6} sm={4}>
            <KpiCard titulo="Compartilhadas" valor={totalCompartilhadas} icone={<PublicIcon sx={{ color: theme.palette.info.main }} />} cor={theme.palette.info.main} bgCor={alpha(theme.palette.info.main, 0.12)} />
          </Grid>
        </Grid>
      )}

      <Paper elevation={0} sx={{ borderRadius: 2, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden' }}>
        <Box sx={{ px: 1.5, py: 0.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: alpha(theme.palette.primary.main, 0.04), borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="body2" fontWeight={700}>Galeria de Figurinhas</Typography>
          {!loading && (
            <Chip size="small" label={`${figurinhas.length} figurinha${figurinhas.length !== 1 ? 's' : ''}`} color="primary" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
          )}
        </Box>
        <Box sx={{ p: 1.5, minHeight: 260 }}>
        {loading ? (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={6} gap={1}>
            <CircularProgress size={32} />
            <Typography variant="caption" color="text.secondary">Carregando figurinhas...</Typography>
          </Box>
        ) : figurinhas.length === 0 ? (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight={220} gap={1}>
            <ImageIcon sx={{ fontSize: 40, color: alpha(theme.palette.text.secondary, 0.25) }} />
            <Typography variant="subtitle2" fontWeight={600}>Nenhuma figurinha cadastrada</Typography>
            <Typography variant="caption" color="text.secondary">Adicione imagens para usar no atendimento.</Typography>
          </Box>
        ) : (
          <Grid container spacing={1.5}>
            {figurinhas.map((figurinha) => (
              <Grid item xs={6} sm={4} md={3} lg={2} key={figurinha.id}>
                <Card elevation={0} sx={{ borderRadius: 2, overflow: 'hidden', opacity: figurinha.ativo ? 1 : 0.55, border: `1px solid ${theme.palette.divider}`, transition: 'transform 0.15s', '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.12)}` } }}>
                  <CardMedia component="img" image={getArquivoUrl(figurinha.caminho_arquivo)} sx={{ height: 128, objectFit: 'contain', bgcolor: alpha(theme.palette.action.hover, 0.5), p: 1 }} />
                  <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={0.5}>
                      <Chip
                        size="small"
                        icon={figurinha.compartilhar ? <PublicIcon sx={{ fontSize: 14 }} /> : <LockIcon sx={{ fontSize: 14 }} />}
                        label={figurinha.compartilhar ? 'Todos' : 'Privada'}
                        color={figurinha.compartilhar ? 'info' : 'default'}
                        sx={{ height: 21, fontSize: '0.68rem' }}
                      />
                      <Box display="flex">
                        {podeEditarFigurinha && (
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => abrirEditar(figurinha)}><EditIcon sx={{ fontSize: 16 }} /></IconButton>
                        </Tooltip>
                        )}
                        {podeDeletarFigurinha && (
                        <Tooltip title="Excluir">
                          <IconButton size="small" color="error" onClick={() => handleExcluir(figurinha)}><DeleteIcon sx={{ fontSize: 16 }} /></IconButton>
                        </Tooltip>
                        )}
                      </Box>
                    </Box>
                    {podeEditarFigurinha && (
                    <>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mt={0.5}>
                      <Typography variant="caption" color="text.secondary">Ativo</Typography>
                      <Switch size="small" checked={figurinha.ativo} onChange={() => handleToggle(figurinha, 'ativo')} />
                    </Box>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Typography variant="caption" color="text.secondary">Compartilhar</Typography>
                      <Switch size="small" checked={figurinha.compartilhar} color="info" onChange={() => handleToggle(figurinha, 'compartilhar')} />
                    </Box>
                    </>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
        </Box>
      </Paper>

      {isMobile && podeAdicionarFigurinha && (
        <Fab color="primary" sx={{ position: 'fixed', bottom: 86, right: 16 }} onClick={abrirNova}>
          <AddIcon />
        </Fab>
      )}

      <Dialog
        open={dialogAberto && (editando ? podeEditarFigurinha : podeAdicionarFigurinha)}
        onClose={fecharDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{editando ? 'Editar Figurinha' : 'Nova Figurinha'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <Button variant="outlined" startIcon={<ImageIcon />} onClick={() => setCropOpen(true)}>
            {form.file ? 'Alterar imagem' : 'Selecionar imagem'}
          </Button>
          {form.preview && (
            <Box component="img" src={form.preview} alt="Preview da figurinha" sx={{ width: '100%', height: 180, objectFit: 'contain', bgcolor: 'action.hover', borderRadius: 1, p: 1 }} />
          )}
          <FormControlLabel control={<Switch checked={form.ativo} onChange={(e) => setForm({ ...form, ativo: e.target.checked })} color="success" />} label="Ativo" />
          <FormControlLabel control={<Switch checked={form.compartilhar} onChange={(e) => setForm({ ...form, compartilhar: e.target.checked })} color="info" />} label="Compartilhar com todos" />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={fecharDialog}>Cancelar</Button>
          <Button variant="contained" onClick={handleSalvar} disabled={salvando || (!editando && !form.file)}>
            {salvando ? <CircularProgress size={18} color="inherit" /> : 'Enviar'}
          </Button>
        </DialogActions>
      </Dialog>

      <ImageUploadCrop
        open={cropOpen}
        onClose={() => setCropOpen(false)}
        onImageSave={handleImageCropSave}
        title="Recortar Figurinha"
        maxWidth={512}
        maxHeight={512}
        quality={0.9}
        outputFormat="auto"
        circularCrop={false}
        minCropSize={50}
      />
    </Box>
  );
};

export default FigurinhasPage;
